import React, { ElementType, useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { HStack, Text, VStack } from '@chakra-ui/react';

import Loader from '../../../../components/Loader';
import {
  useArticlesQuery,
  useDashboardExploreStudyGroupsQuery,
} from '../../../../generated/graphqlModels';
import ClassComponent from '../../components/ClassComponent';
import { AuthContext, Role } from '../../../../context/AuthProvider';
import { useQuery } from '@apollo/client';
import { GET_ARTICLE_READS } from '../Article/queries';

export const ExploreGroups: Record<
  string,
  {
    en_name: string;
    name: string;
    en_description: string;
    description: string;
    image: any;
  }
> = {
  blog: {
    en_name: 'Explore STEM articles',
    name: 'Istraži STEM članke',
    en_description:
      'Step into the world of science and technology through popular articles from the leading technology media.',
    description:
      'Zakorači u svijet znanosti i tehnologije kroz popularne članke vodećih Hrvatskih tehnoloških medija Bug i Netokracija.',
    image: '/static/ui/news-icon.png',
  },
  stemCareers: {
    en_name: 'Meet technology companies',
    name: 'Upoznaj tehnološke tvrtke',
    en_description:
      "Meet the leading companies that are changing the way we live, work and communicate. Step into their offices and find out what it's like to work in tech companies.",
    description:
      'Upoznaj se s vodećim tvrtkama koje mijenjanju način na koji živimo, radimo i komuniciramo. Zakorači u njihove urede i saznaj kako izgleda raditi u tech tvrtkama.',
    image: '/static/ui/tech-companies-icon.png',
  },
  stemFaculty: {
    en_name: 'Meet the STEM faculties',
    name: 'Upoznaj STEM fakultete',
    en_description:
      'Choosing a college is one of the most important decisions you will have to make after high school, so start doing your research early. Get to know the STEM faculties, see what their laboratories look like and what cool things and projects you can work on.',
    description:
      'Odabir fakulteta jedna je od najvažnijih odluka koje ćeš morati donijeti po završetku srednje škole zato počni istraživati na vrijeme. Upoznaj STEM fakultete, pogledaj kako izgledaju njihovi laboratoriji te na kojim sve cool stvarima i projektima možeš raditi.',
    image: '/static/ui/university-icon.png',
  },
  startup: {
    en_name: 'How to build a technology company?',
    name: 'Kako napraviti tehnološku tvrtku?',
    en_description:
      'Have you ever imagined what the first steps in creating a technology company look like? Here you can find out how STEMI came about and how a group of high school students started the Mundus project.',
    description:
      'Jesi li ikad zamišljao kako izgledaju prvi koraci u stvaranju tehnološke tvrtke? Ovdje možeš saznati kako je nastao STEMI te kako je grupa srednjoškolaca pokrenula projekt Mundus.',
    image: '/static/ui/startup-icon.png',
  },
  engineerJob: {
    en_name: 'Engineering jobs',
    name: 'Inžinjerski poslovi',
    en_description: 'Get to know the news through interesting articles',
    description: 'Upoznaj se s novostima kroz zanimljive članke',
    image: '/static/ui/tech-companies-icon.png',
  },
  goatTalk: {
    en_name: 'GOAT talks',
    name: 'GOAT talks',
    en_description: 'Hear conversations with the best of the best',
    description: 'Poslušaj razgovore s najboljima od najboljih',
    image: '/static/ui/goat.png',
  },
  podcast: {
    en_name: 'Podcasts',
    name: 'Podcasts',
    en_description: 'Hear about interesting topics from experts',
    description: 'Poslušaj o zanimljivim temama od stručnjaka',
    image: '/static/ui/podcast.png',
  },
};

interface ExploreProps {
  onNewBlog?: (count: number) => void;
}

const Explore: React.FC<ExploreProps> = (props) => {
  const { onNewBlog } = props;
  const location = useLocation();
  const user = useContext(AuthContext);

  const { data, loading } = useArticlesQuery();
  const { data: articleReadsData } = useQuery(GET_ARTICLE_READS, {
    variables: { userId: user?.pk },
  });
  const articleTypes =
    data?.articles?.reduce((acc, cur) => {
      if (!acc.includes(cur.type)) {
        acc.push(cur.type);
      }
      return acc;
    }, [] as string[]) || [];

  const articleReadsIds =
    articleReadsData?.articleReads?.map((a: any) => a.articleId) || [];

  useEffect(() => {
    if (data?.articles && articleReadsData?.articleReads) {
      const userArticles = data.articles.filter((a) =>
        user?.namespaces.includes(a.namespace),
      );
      const unreadArticles = userArticles.filter(
        (a) => !articleReadsIds.includes(a.id),
      );
      if (onNewBlog) {
        onNewBlog(unreadArticles.length);
      }
    }
  }, [data, articleReadsIds]);

  if (!user || !user.pk) {
    return null;
  }

  const isUserDemoOrSpecificNamespace =
    user.is_demo ||
    ((user.namespaces.includes('tennessee') ||
      user.namespaces.includes('qatar')) &&
      !user.roles.includes(Role.staff));

  return (
    <>
      {loading && <Loader />}
      <VStack mt="-8">
        <HStack
          align="stretch"
          pt="8"
          w="full"
          h="full"
          mx="-8px"
          flexWrap="wrap"
          alignContent="stretch"
          spacing="0px"
          style={{
            justifyContent: loading ? 'center' : 'flex-start',
          }}
        >
          {articleTypes.map((articleType, index) => {
            const articles =
              data?.articles.filter(
                (a) =>
                  a.type === articleType &&
                  user.namespaces.includes(a.namespace),
              ) || [];
            const unreadArticles = articles.filter(
              (article) => !articleReadsIds.includes(article.id),
            );
            const exploreGroup = ExploreGroups[articleType] || ExploreGroups[0];
            const GroupImage = exploreGroup?.image
              ? () => <img src={exploreGroup?.image} />
              : null;

            return (
              <VStack
                px="2"
                w="33.3%"
                flexBasis={['100%', '100%', '50%', '33.3%']}
                key={index}
                style={{
                  boxSizing: 'border-box',
                  position: 'relative',
                }}
                gap={4}
              >
                <ClassComponent
                  to={'/article/' + articles[0]?.id}
                  programId={''}
                  classId={''}
                  isUnit
                  isStaff={false}
                  isTeacherOrStaff={false}
                  canBeShared={1}
                  isPublished={true}
                  enabled={isUserDemoOrSpecificNamespace ? false : true}
                  name={
                    isUserDemoOrSpecificNamespace
                      ? exploreGroup?.en_name
                      : exploreGroup?.name
                  }
                  description={
                    isUserDemoOrSpecificNamespace
                      ? exploreGroup?.en_description
                      : exploreGroup?.description
                  }
                  ImageComponent={GroupImage ? () => <GroupImage /> : undefined}
                  newItems={unreadArticles.length || 0}
                />
              </VStack>
            );
          })}
        </HStack>
      </VStack>
    </>
  );
};

export default Explore;
export { Explore };
